import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import PricingCard, { PricingFeature } from "../components/PricingCard";
import useLemonSqueezySubscription, { LemonSqueezyPrice } from "../hooks/useLemonSqueezySubscription";
import { TierName } from "../types/AIModel";
import { storage } from "../utils/storage";
import { useUserStore } from "../store/userStore";

// Platform display names
const PLATFORM_LABELS: Record<string, string> = {
  apple: 'Apple App Store',
  google: 'Google Play Store',
  stripe: 'Web (Stripe)',
  paddle: 'Web (Paddle)',
  lemonsqueezy: 'Web (Lemon Squeezy)',
};

// Feature lists for each tier
const TIER_FEATURES: Record<TierName, PricingFeature[]> = {
  free: [
    { text: "20 messages per day", included: true },
    { text: "5 conversations per day", included: true },
    { text: "Access to FREE AI models", included: true },
    { text: "Image & PDF upload (10MB)", included: true },
    { text: "Basic chat features", included: true },
    { text: "Advanced AI models", included: false },
    { text: "Audio file support", included: false },
    { text: "Video file support", included: false },
    { text: "Priority support", included: false },
  ],
  basic: [
    { text: "200 messages per day", included: true, highlight: true },
    { text: "Unlimited conversations", included: true, highlight: true },
    { text: "Access to FREE + BASIC AI models", included: true },
    { text: "Image & PDF upload (50MB)", included: true },
    { text: "Audio file support (15 min)", included: true, highlight: true },
    { text: "100 PDF pages limit", included: true },
    { text: "Ad-free experience", included: true },
    { text: "Video file support", included: false },
    { text: "Priority support", included: false },
  ],
  pro: [
    { text: "Unlimited messages", included: true, highlight: true },
    { text: "Unlimited conversations", included: true, highlight: true },
    { text: "Access to ALL AI models", included: true, highlight: true },
    { text: "Large file upload (500MB)", included: true },
    { text: "Audio support (2 hours)", included: true },
    { text: "Video support (1 hour)", included: true, highlight: true },
    { text: "500 PDF pages limit", included: true },
    { text: "Ad-free experience", included: true },
    { text: "Priority support", included: true, highlight: true },
  ],
};

const Pricing: React.FC = () => {
  console.log('🟢 [Pricing.tsx] Rendering Pricing page (NEW)');
  const navigate = useNavigate();
  const [selectedInterval, setSelectedInterval] = useState<"month" | "year">("month");
  const [loadingTier, setLoadingTier] = useState<TierName | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  const {
    prices,
    loadingPrices,
    pricesError,
    subscription: lemonSqueezySubscription,
    loadingSubscription,
    openCheckout,
    getPortal,
    error: lemonSqueezyError,
  } = useLemonSqueezySubscription();

  const { subscription, fetchSubscription } = useUserStore();

  // Check if user has subscription from Apple/Google (can't manage via web)
  const hasNonWebSubscription = subscription?.hasActiveSubscription &&
    subscription.platform &&
    subscription.platform !== 'stripe' &&
    subscription.platform !== 'paddle' &&
    subscription.platform !== 'lemonsqueezy';

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Organize prices by tier and interval
  const pricesByTier = useMemo(() => {
    const result: Record<TierName, { monthly: LemonSqueezyPrice | null; yearly: LemonSqueezyPrice | null }> = {
      free: { monthly: null, yearly: null },
      basic: { monthly: null, yearly: null },
      pro: { monthly: null, yearly: null },
    };

    prices.forEach((price) => {
      const tier = price.tier as TierName;
      if (tier && result[tier]) {
        if (price.interval === "month") {
          result[tier].monthly = price;
        } else if (price.interval === "year") {
          result[tier].yearly = price;
        }
      }
    });

    return result;
  }, [prices]);

  const handleSelectPlan = async (tier: TierName) => {
    const token = storage.getJwtToken();
    if (!token) {
      navigate("/get-started");
      return;
    }

    // If user already has active Lemon Squeezy subscription, open portal
    if (lemonSqueezySubscription?.status === 'active' && lemonSqueezySubscription.tier !== 'free') {
      const portal = await getPortal();
      if (portal?.customerPortalUrl) {
        window.open(portal.customerPortalUrl, '_blank');
      }
      return;
    }

    // Get the price for selected tier and interval
    const tierPrices = pricesByTier[tier];
    const price = selectedInterval === "month" ? tierPrices.monthly : tierPrices.yearly;

    if (!price) {
      alert("This plan is not available. Please try another option.");
      return;
    }

    setLoadingTier(tier);

    // Open Lemon Squeezy checkout (will redirect to their checkout page)
    await openCheckout(price.id);

    setLoadingTier(null);
  };

  const handleManageSubscription = async () => {
    const portal = await getPortal();
    if (portal?.customerPortalUrl) {
      window.open(portal.customerPortalUrl, '_blank');
    }
  };

  const currentTier = lemonSqueezySubscription?.tier || subscription?.tier || "free";

  // Check if prices are loaded and available
  const hasPrices = prices.length > 0;

  // Calculate average savings percentage for yearly plans
  const yearlySavingsPercent = useMemo(() => {
    const tiers: TierName[] = ['basic', 'pro'];
    let totalSavings = 0;
    let count = 0;

    tiers.forEach(tier => {
      const monthly = pricesByTier[tier].monthly?.amount;
      const yearly = pricesByTier[tier].yearly?.amount;

      if (monthly && yearly && monthly > 0) {
        const yearlyIfMonthly = monthly * 12;
        const savings = ((yearlyIfMonthly - yearly) / yearlyIfMonthly) * 100;
        if (savings > 0) {
          totalSavings += savings;
          count++;
        }
      }
    });

    return count > 0 ? Math.round(totalSavings / count) : 20; // Default 20% if no prices
  }, [pricesByTier]);

  if (loadingPrices || loadingSubscription) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl">Loading pricing...</div>
      </div>
    );
  }

  // Show message for Apple/Google subscribers
  if (hasNonWebSubscription) {
    const platformLabel = PLATFORM_LABELS[subscription?.platform || ''] || subscription?.platform;
    const formatDate = (dateString: string | null) => {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    };

    return (
      <div className="min-h-screen bg-black py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <button
            onClick={() => navigate(-1)}
            className="absolute top-4 left-4 text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="bg-[#1a1a1a] rounded-2xl p-8 mt-16">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#3af062]/20 flex items-center justify-center">
              <svg className="w-10 h-10 text-[#3af062]" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>

            <h1 className="text-3xl font-bold text-white mb-4">
              You're on {subscription?.tier?.toUpperCase()} Plan
            </h1>

            <p className="text-gray-400 mb-6">
              Your subscription is managed via <span className="text-white font-medium">{platformLabel}</span>
            </p>

            <div className="bg-black/50 rounded-xl p-4 mb-6">
              <div className="flex justify-between mb-2">
                <span className="text-gray-400">Status</span>
                <span className="text-[#3af062] font-medium">
                  {subscription?.willExpire ? 'Canceling' : 'Active'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">
                  {subscription?.willExpire ? 'Access Until' : 'Renews On'}
                </span>
                <span className="text-white">{formatDate(subscription?.expiresAt || null)}</span>
              </div>
            </div>

            {subscription?.willExpire && (
              <p className="text-yellow-500 text-sm mb-6">
                Your subscription is canceled and will expire on {formatDate(subscription?.expiresAt || null)}.
                After that, you can subscribe via web.
              </p>
            )}

            <div className="bg-gray-800/50 rounded-xl p-4 text-left">
              <h3 className="text-white font-medium mb-2">To manage your subscription:</h3>
              {subscription?.platform === 'apple' && (
                <p className="text-gray-400 text-sm">
                  Open <span className="text-white">Settings</span> on your iPhone/iPad →{' '}
                  <span className="text-white">Apple ID</span> →{' '}
                  <span className="text-white">Subscriptions</span> → MixxAI
                </p>
              )}
              {subscription?.platform === 'google' && (
                <p className="text-gray-400 text-sm">
                  Open <span className="text-white">Google Play Store</span> →{' '}
                  <span className="text-white">Menu</span> →{' '}
                  <span className="text-white">Subscriptions</span> → MixxAI
                </p>
              )}
            </div>

            <button
              onClick={() => navigate('/')}
              className="mt-8 px-8 py-3 rounded-full bg-[#3af062] text-black font-bold hover:bg-[#3af062]/90 transition-all"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <button
            onClick={() => navigate(-1)}
            className="absolute top-4 left-4 text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Choose Your Plan
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Unlock the full power of AI with our premium plans. Cancel anytime.
          </p>

          {/* Billing interval toggle */}
          <div className="mt-8 inline-flex items-center bg-[#1a1a1a] rounded-full p-1">
            <button
              onClick={() => setSelectedInterval("month")}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                selectedInterval === "month"
                  ? "bg-[#3af062] text-black"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setSelectedInterval("year")}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                selectedInterval === "year"
                  ? "bg-[#3af062] text-black"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              Yearly
              <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                selectedInterval === "year"
                  ? "bg-black/30 text-black"
                  : "bg-[#3af062]/20 text-[#3af062]"
              }`}>
                Save {yearlySavingsPercent}%
              </span>
            </button>
          </div>
        </div>

        {pricesError && (
          <div className="text-center text-yellow-500 mb-8">
            Unable to load prices from server. Showing default pricing.
          </div>
        )}

        {lemonSqueezyError && (
          <div className="text-center text-red-500 mb-8">
            {lemonSqueezyError}
          </div>
        )}

        {/* Pricing cards - Pro (Best Value) first */}
        <div
          className={`grid gap-6 ${
            isMobile ? "grid-cols-1" : "md:grid-cols-3"
          } items-stretch`}
        >
          {/* Pro Plan - Best Value (First) */}
          <PricingCard
            tier="pro"
            name="Pro"
            monthlyPrice={pricesByTier.pro.monthly?.amount ?? 14.99}
            yearlyPrice={pricesByTier.pro.yearly?.amount ?? 149.99}
            currency={pricesByTier.pro.monthly?.currency || pricesByTier.pro.yearly?.currency}
            features={TIER_FEATURES.pro}
            isCurrentPlan={currentTier === "pro"}
            selectedInterval={selectedInterval}
            onSelect={() => handleSelectPlan("pro")}
            loading={loadingTier === "pro"}
            disabled={!hasPrices}
            popular
          />

          {/* Basic Plan */}
          <PricingCard
            tier="basic"
            name="Basic"
            monthlyPrice={pricesByTier.basic.monthly?.amount ?? 4.99}
            yearlyPrice={pricesByTier.basic.yearly?.amount ?? 49.99}
            currency={pricesByTier.basic.monthly?.currency || pricesByTier.basic.yearly?.currency}
            features={TIER_FEATURES.basic}
            isCurrentPlan={currentTier === "basic"}
            selectedInterval={selectedInterval}
            onSelect={() => handleSelectPlan("basic")}
            loading={loadingTier === "basic"}
            disabled={!hasPrices}
          />

          {/* Free Plan */}
          <PricingCard
            tier="free"
            name="Free"
            monthlyPrice={0}
            yearlyPrice={0}
            features={TIER_FEATURES.free}
            isCurrentPlan={currentTier === "free"}
            selectedInterval={selectedInterval}
          />
        </div>

        {/* Manage subscription link for existing Lemon Squeezy subscribers */}
        {lemonSqueezySubscription?.status === 'active' && lemonSqueezySubscription.tier !== 'free' && (
          <div className="text-center mt-8">
            <button
              onClick={handleManageSubscription}
              className="text-[#3af062] hover:underline"
            >
              Manage your subscription
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="mt-16 text-center">
          <div className="flex flex-wrap justify-center gap-8 text-gray-500 text-sm">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              Secure payment via Lemon Squeezy
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                  clipRule="evenodd"
                />
              </svg>
              Cancel anytime
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z" />
                <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z" />
              </svg>
              Instant activation
            </div>
          </div>

          <p className="mt-8 text-gray-600 text-xs">
            By subscribing, you agree to our Terms of Service and Privacy Policy.
            <br />
            Prices shown are in {pricesByTier.basic.monthly?.currency || pricesByTier.pro.monthly?.currency || 'VND'}. Taxes may apply based on your location.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
